import {noop} from 'lodash';
import React, {FC, useState} from 'react';

import {Trans} from '../../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {EventHandler} from '../../../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {TeammateModel} from '../../../../../../../libs/shared/core/src/models/api/teammateModel';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../../../../../libs/shared/core/src/services/tracking/tracking';
import {makeBannerUid} from '../../../../../../../libs/shared/core/src/store/banners/bannerHelpers';
import {
  bannerUndoDelay,
  showBannerWithAutomaticRemovalAction,
} from '../../../../../../../libs/shared/core/src/store/banners/commonBannerActions';
import {BannerTypesEnum} from '../../../../../../../libs/shared/core/src/store/banners/commonBannerState';
import {InfoBannerTypesEnum} from '../../../../../../../libs/shared/core/src/store/banners/commonBannerTypes';
import {getHasAiRemoveInaccurateTag} from '../../../../../../../libs/shared/core/src/store/featureFlags/featureFlagsSelectors';
import {ViewerPanelId} from '../../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {Icon} from '../../../../../../../libs/shared/ui/src/components/icon/icon';
import {
  NavigateToLinkMethod,
  useLinkNavigation,
} from '../../../../../../../libs/shared/ui/src/components/navigation/useLinkNavigation';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {UserThemeConsumer} from '../../../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {useAppDispatch, useAppSelector} from '../../../../../../helpers/appReduxHelpers';
import {BulkActionTypesEnum} from '../../../../../../helpers/bulkActionHelpers';
import {getColorHighlight} from '../../../../../../helpers/models/highlightHelpers';
import {ConversationAiMarkModel} from '../../../../../../models/api/conversationAiMarkModel';
import {ConversationModel} from '../../../../../../models/api/conversationModel';
import {PermissionsEnum} from '../../../../../../models/api/roleModel';
import {TagModel} from '../../../../../../models/api/tagModel';
import {SettingsTagTabsEnum} from '../../../../../../models/url/settingsUrlModel';
import {makeSettingsUrlFromPathId} from '../../../../../../settings/menu/settingsHelpers';
import {AiTaggingReviewManualDiscardAsyncAction} from '../../../../../../settings/rules/ruleEditor/recipes/aiTagging/state/aiTaggingEditorActions';
import {InboxesVisualConversationSelection} from '../../../../../../store/panels/cells/helpers/cellsPanelSelectionHelpers';
import {EditTagDropdownContainer} from '../../../../../common/dropdowns/tags/editTagDropdownContainer';
import {AiTaggingInaccurateDialogContainer} from '../../../dialogs/aiTaggingInaccurateDialogContainer';
import {BulkActionConfirmationDialog} from '../../actions/bulk/bulkActionConfirmationDialog';
import {ConversationHeaderPill, StyledPopoverCoordinatorWrapperDiv} from '../conversationHeaderPill';
import {ConversationHeaderTagPillContent} from './conversationHeaderTagPillContent';
import {ConversationHeaderTagPillTooltip} from './conversationHeaderTagPillTooltip';
import {TagDropdownContainer} from './tagDropdownContainer';

/*
 * Props.
 */

export interface ConversationHeaderTagPillProps {
  panelId: ViewerPanelId;
  userId: number;
  teammate: TeammateModel;
  tag: TagModel;
  tagsPath: ReadonlyArray<TagModel>;
  isLastPrivateTag: boolean;
  wasAddedByAi: boolean;
  aiMark?: ConversationAiMarkModel;
  conversation?: ConversationModel;
  teammates: ReadonlyArray<TeammateModel>;
  hasPermission: (namespace: string, permission: PermissionsEnum) => boolean;
  isBulkActionRequired: boolean;
  onUntag: () => void;
  conversationSelection: InboxesVisualConversationSelection;
}

/*
 * Component.
 */

export const ConversationHeaderTagPill: FC<ConversationHeaderTagPillProps> = (props) => {
  const {panelId, aiMark, conversation} = props;
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isBulkConfirmOpen, setIsBulkConfirmOpen] = useState(false);
  const {navigateToLink} = useLinkNavigation();
  const [isShowingInaccurateDialog, setIsShowingInaccurateDialog] = useState(false);
  const hasAiRemoveInaccurateTag = useAppSelector(getHasAiRemoveInaccurateTag);
  const dispatch = useAppDispatch();
  const onRemoveInaccurateTagClick = () => {
    const now = Date.now();
    if (conversation && aiMark) {
      trackInterfaceEvent(InterfaceEventsEnum.P3_AI_REPORT_INACCURATE_TAG_CLICK, {
        tag_id: props.tag.id,
        conversation_id: conversation.id,
      });
      dispatch(AiTaggingReviewManualDiscardAsyncAction(conversation.id, aiMark, undefined));
      dispatch(
        showBannerWithAutomaticRemovalAction({
          type: BannerTypesEnum.INFO,
          infoType: InfoBannerTypesEnum.AI_REPORT_INACCURATE_TAG,
          tagId: props.tag.id,
          conversationId: conversation.id,
          uid: makeBannerUid(),
          createdAt: now,
          expiresAt: now + bannerUndoDelay,
        }),
      );
    }
  };

  return (
    <>
      <StyledPopoverCoordinatorWrapperDiv>
        <PopoverCoordinator
          interactionId="tag-header-pill"
          renderButton={(isOpen) => renderPill(isOpen, props)}
          renderModal={(popoverProps) =>
            renderDropdown(
              popoverProps,
              props,
              isEditOpen,
              setIsEditOpen,
              isBulkConfirmOpen,
              setIsBulkConfirmOpen,
              navigateToLink,
              () => {
                if (hasAiRemoveInaccurateTag) {
                  onRemoveInaccurateTagClick();
                  return;
                }

                setIsShowingInaccurateDialog(true);
              },
            )
          }
        />
      </StyledPopoverCoordinatorWrapperDiv>
      {isShowingInaccurateDialog && conversation && aiMark && (
        <AiTaggingInaccurateDialogContainer
          panelId={panelId}
          tag={props.tag}
          tagsPath={props.tagsPath}
          aiMark={aiMark}
          conversation={conversation}
          onRequestClose={() => setIsShowingInaccurateDialog(false)}
        />
      )}
    </>
  );
};

/*
 * Dropdown.
 */

function renderDropdown(
  popoverProps: PopoverTransferProps,
  props: ConversationHeaderTagPillProps,
  isEditOpen: boolean,
  setIsEditOpen: (val: boolean) => void,
  isBulkConfirmOpen: boolean,
  setIsBulkConfirmOpen: (val: boolean) => void,
  navigateToLink: NavigateToLinkMethod,
  onShowInaccurateDialog: () => void,
) {
  if (isEditOpen) {
    return renderEditDialog(popoverProps, props, setIsEditOpen);
  }

  if (isBulkConfirmOpen) {
    return renderBulkConfirmDialog(popoverProps, props, setIsBulkConfirmOpen);
  }

  return renderListDropdown(
    popoverProps,
    props,
    setIsEditOpen,
    setIsBulkConfirmOpen,
    navigateToLink,
    onShowInaccurateDialog,
  );
}

/*
 * Pill.
 */

function renderPill(isOpen: boolean, props: ConversationHeaderTagPillProps) {
  const {teammate, tag, tagsPath, wasAddedByAi} = props;

  // Special appearance for the Starred tag when displayed.
  if (tag.id === teammate.starredTagId) {
    return (
      <UserThemeConsumer>
        {(theme) => (
          <ConversationHeaderPill
            hasHover={isOpen}
            isPrivate
            renderIcon={() => <Icon name="starFilledSmall" color={theme.palette.yellow.shade40} />}
          >
            <Trans id="tags-starredtag">Star</Trans>
          </ConversationHeaderPill>
        )}
      </UserThemeConsumer>
    );
  }

  // Special appearance for the Inbox tag.
  if (tag.isMainInboxTag) {
    return (
      <ConversationHeaderPill hasHover={isOpen} isPrivate renderIcon={() => <Icon name="inboxFilledSmall" />}>
        <Trans id="tags-inboxtag">Inbox</Trans>
      </ConversationHeaderPill>
    );
  }

  const color = getColorHighlight(tag);
  const isPrivate = tag.namespace === teammate.namespace;

  if (wasAddedByAi) {
    return (
      <ConversationHeaderPill
        hasHover={isOpen}
        color={color}
        isPrivate={isPrivate}
        renderTooltip={() => (!isOpen || Boolean(tag.description)) && renderPillTooltip(tagsPath)}
        renderIcon={() => <Icon name="sparklesAiFilledTiny" />}
        shouldRenderIconWithInvertedBackground
      >
        <ConversationHeaderTagPillContent tagsPath={tagsPath} />
      </ConversationHeaderPill>
    );
  }

  // Normal case for any other tag.
  return (
    <ConversationHeaderPill
      hasHover={isOpen}
      color={color}
      isPrivate={isPrivate}
      renderTooltip={() => (!isOpen || Boolean(tag.description)) && renderPillTooltip(tagsPath)}
    >
      <ConversationHeaderTagPillContent tagsPath={tagsPath} />
    </ConversationHeaderPill>
  );
}

function renderPillTooltip(tagsPath: ReadonlyArray<TagModel>) {
  return <ConversationHeaderTagPillTooltip tagsPath={tagsPath} />;
}

/*
 * Tag details dropdown.
 */

function renderListDropdown(
  popoverProps: PopoverTransferProps,
  props: ConversationHeaderTagPillProps,
  setIsEditOpen: (val: boolean) => void,
  setIsBulkConfirmOpen: (val: boolean) => void,
  navigateToLink: NavigateToLinkMethod,
  onShowInaccurateDialog: () => void,
) {
  const onEditClick: EventHandler = (e) => {
    e.preventDefault(); // Keep the dropdown open
    if (props.tag.archivedAt) {
      const tagUrl = makeSettingsUrlFromPathId(
        'settings.new-tags',
        {
          namespace: props.tag.namespace,
        },
        {
          page: SettingsTagTabsEnum.ARCHIVED,
          search: props.tag.name,
        },
      );
      navigateToLink(tagUrl);
    } else {
      setIsEditOpen(true); // Switch to Edit Tag
    }
  };

  const onUntagClick: EventHandler = (e) => {
    if (props.isBulkActionRequired) {
      e.preventDefault();
      setIsBulkConfirmOpen(true);
      return;
    }

    props.onUntag();
  };

  return (
    <TagDropdownContainer
      {...popoverProps}
      panelId={props.panelId}
      teammate={props.teammate}
      tag={props.tag}
      isLastPrivateTag={props.isLastPrivateTag}
      onEditClick={onEditClick}
      onUntagClick={onUntagClick}
      // We only show tags that belong to the conversations in the header,
      // so we know we'll never need to add them.
      isAddedToConversations
      onAddTagClick={noop}
      wasAddedByAi={props.wasAddedByAi}
      onShowInaccurateDialog={onShowInaccurateDialog}
      selectedConversationIds={props.conversationSelection.knownIds}
    />
  );
}

/*
 * Tag edit dialog.
 */

function renderEditDialog(
  popoverProps: PopoverTransferProps,
  props: ConversationHeaderTagPillProps,
  setIsEditOpen: (val: boolean) => void,
) {
  const onRequestClose = () => {
    popoverProps.onRequestClose(); // Close the dropdown
    setIsEditOpen(false); // Reset back to List
  };

  return (
    <EditTagDropdownContainer
      {...props}
      {...popoverProps}
      onRequestClose={onRequestClose}
      onBackButtonClick={() => setIsEditOpen(false)}
    />
  );
}

function renderBulkConfirmDialog(
  popoverProps: PopoverTransferProps,
  props: ConversationHeaderTagPillProps,
  setIsBulkConfirmOpen: (val: boolean) => void,
) {
  const onRequestClose = () => {
    popoverProps.onRequestClose();
    setIsBulkConfirmOpen(false);
  };
  return (
    <BulkActionConfirmationDialog
      onConfirmClick={props.onUntag}
      onRequestClose={onRequestClose}
      actionType={BulkActionTypesEnum.UNTAG}
    />
  );
}
